import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import { useUserSettingsStore } from '@/stores/user-settings.store';
import { useHistoryStore } from '@/stores/history.store';
import keyBy from 'lodash/keyBy';
import get from 'lodash/get';
import set from 'lodash/set';
import Vue from 'vue';
import moment, { DATE_FORMAT } from '@/utils/moment-wrapper.ts';
import {
    WORKFLOWS_SUB_USER_SETTING_NAME,
    WORKFLOW_USER_SETTINGS,
    DEFAULT_WORKFLOW_MAX_DURATION,
    DEFAULT_WORKFLOW_MAX_INTERVAL,
} from '@/workflows/constants/workflow.constants';
import { getDefaultTimeseries } from '@/reports/utils';
import { HISTORY_TYPES } from '@/vuex/modules/user-settings/history/history.constants.js';
import {
    fetchAllWorkflowsForSubscription,
    fetchWorkflowForSubscription,
    createWorkflow,
    deleteWorkflow,
    updateWorkflow,
} from '@/vuex/modules/saved-reports/saved-reports.utils';

let fetchPromise;

function getInitialState () {
    fetchPromise = null;

    return {
        isFetching: false,
        isRemoving: false,
        isSaving: false,
        map: {},
        includedSteps: {},
        savedWorkflowId: null,
        workingWorkflow: {},
        insightsShowing: false,
    };
}

// Use to heal workflows that were created before the Pendo One migration
function augmentWorkflowWithDefaults (workflow) {
    if (!workflow.timeSeries) workflow.timeSeries = getDefaultTimeseries('Workflow');
    if (!workflow.segmentId) workflow.segmentId = 'everyone';
}

const defaultWorkflowSettings = Object.freeze({
    [WORKFLOW_USER_SETTINGS.DATA_SOURCE]: '',
    [WORKFLOW_USER_SETTINGS.TIME_PERIOD]: '',
    [WORKFLOW_USER_SETTINGS.TOTAL_CHART_DATA_SOURCE]: '',
    [WORKFLOW_USER_SETTINGS.GUIDE_MONITOR_OVER_TIME_CHART]: '',
    [WORKFLOW_USER_SETTINGS.TIME_TO_COMPLETE_CHART_DATA_TYPE]: '',
    [WORKFLOW_USER_SETTINGS.TIME_TO_COMPLETE_CHART_TIME_PERIOD]: '',
    [WORKFLOW_USER_SETTINGS.VIEW_BY]: {},
    [WORKFLOW_USER_SETTINGS.BASELINE_DATE_RANGE]: null,
});

const getInitialWorkflowState = (nowDate) => {
    return {
        classification: 'nonrecurring',
        id: null,
        maxDuration: DEFAULT_WORKFLOW_MAX_DURATION,
        maxInterval: DEFAULT_WORKFLOW_MAX_INTERVAL,
        name: `New Workflow - ${moment(nowDate).format(DATE_FORMAT.short)} @ ${moment(nowDate).format(
            DATE_FORMAT.time,
        )}`,
        segmentId: 'everyone',
        subscriptionSharing: {
            permission: 'restricted',
        },
        timeSeries: getDefaultTimeseries('Workflow'),
        workflowSteps: [],
        journey: {
            featureIds: [],
            pageIds: [],
        },
    };
};

export const state = getInitialState();

export const mutations = {
    deleteMapKey (state, { id }) {
        Vue.delete(state.map, id);
    },
    setFetching (state, { isFetching }) {
        state.isFetching = isFetching;
    },
    setIncludedSteps (state, { updatedSteps }) {
        state.includedSteps = cloneDeep(updatedSteps);
    },
    setMap (state, { map }) {
        Vue.set(state, 'map', map);
    },
    setMapAtKey (state, { id, workflow }) {
        Vue.set(state.map, id, workflow);
    },
    setRemoving (state, { isRemoving }) {
        state.isRemoving = isRemoving;
    },
    setSaving (state, { isSaving }) {
        state.isSaving = isSaving;
    },
    setSavedWorkflowId (state, { id }) {
        state.savedWorkflowId = id;
    },
    setWorkingWorkflow (state, { workflow = getInitialWorkflowState(new Date()) } = {}) {
        state.workingWorkflow = workflow;
    },
    setWorkingWorkflowField (state, { field, value }) {
        // Vue.set is not reactive for nested fields, e.g. workingWorkflow.subscriptionSharing.permission
        const workingWorkflow = cloneDeep(state.workingWorkflow);
        set(workingWorkflow, field, value);
        state.workingWorkflow = workingWorkflow;
    },
    showInsights (state) {
        state.insightsShowing = true;
    },
    hideInsights (state) {
        state.insightsShowing = false;
    },
    toggleInsights (state) {
        state.insightsShowing = !state.insightsShowing;
    },
};

export const actions = {
    async loadAll ({ commit, state }, { useCache = true, excludeJourneyDefinition = true } = {}) {
        if (state.isFetching) {
            await fetchPromise;

            return;
        }

        if (!isEmpty(state.map) && useCache) {
            return;
        }

        commit('setFetching', { isFetching: true });

        fetchPromise = fetchAllWorkflowsForSubscription(excludeJourneyDefinition);

        const list = await fetchPromise;
        list.forEach((workflow) => {
            if (workflow.journey) workflow.journey = { pageIds: [], featureIds: [] };
            augmentWorkflowWithDefaults(workflow);
        });

        commit('setMap', { map: keyBy(list, 'id') });
        commit('setFetching', { isFetching: false });
    },
    async loadOne ({ commit }, { id }) {
        let workflow;
        try {
            workflow = await fetchWorkflowForSubscription(id);
        } catch (error) {
            throw error;
        }
        augmentWorkflowWithDefaults(workflow);
        commit('setMapAtKey', { id, workflow });
    },
    async loadByIds ({ commit }, ids) {
        try {
            const workflows = await Promise.all(
                ids.map(async (id) => {
                    try {
                        const workflow = await fetchWorkflowForSubscription(id);

                        return workflow;
                    } catch {
                        return null;
                    }
                }),
            );
            workflows
                .filter((workflow) => workflow !== null)
                .forEach((workflow) => {
                    augmentWorkflowWithDefaults(workflow);
                    commit('setMapAtKey', { id: workflow.id, workflow });
                });
        } catch (error) {
            throw error;
        }
    },
    async create ({ commit }, { workflow } = {}) {
        commit('setSaving', { isSaving: true });
        try {
            const newWorkflow = await createWorkflow(workflow);

            const queryParams = new URLSearchParams(window.location.search);
            const globalCreate = queryParams.has('globalCreate');
            window.pendo?.track('report-created', {
                type: 'Workflow',
                ...(globalCreate && { location: 'globalCreate' }),
            });

            commit('setMapAtKey', {
                id: newWorkflow.id,
                workflow: newWorkflow,
            });
            commit('setSaving', { isSaving: false });

            return newWorkflow.id;
        } catch (error) {
            commit('setSaving', { isSaving: false });
            throw error;
        }
    },
    async update ({ commit, state }, { workflow, applyToAggregation }) {
        commit('setSaving', { isSaving: true });
        try {
            augmentWorkflowWithDefaults(workflow);
            const updatedWorkflow = await updateWorkflow(state.map[workflow.id], workflow, applyToAggregation);
            commit('setMapAtKey', {
                id: updatedWorkflow.id,
                workflow: updatedWorkflow,
            });
        } catch (error) {
            throw error;
        } finally {
            commit('setSaving', { isSaving: false });
        }
    },
    async remove ({ commit }, { id }) {
        commit('setRemoving', { isRemoving: true });
        try {
            await deleteWorkflow(id);
            commit('deleteMapKey', { id });
        } catch (error) {
            throw error;
        } finally {
            commit('setRemoving', { isRemoving: false });
        }
    },
    toggleIncludedStep ({ commit, state }, step) {
        const updatedSteps = cloneDeep(state.includedSteps);
        if (step.stepId) {
            step.id = step.stepId;
            step.label = step.name;
            step.type = step.featureId ? 'featureId' : 'pageId';
        }

        if (updatedSteps[step.id]) {
            delete updatedSteps[step.id];
        } else {
            // Only save the fields we care about
            updatedSteps[step.id] = {
                id: step.id,
                label: step.name,
                type: step.type,
            };
        }

        commit('setIncludedSteps', { updatedSteps });
    },
    updateWorkflowSetting (_context, { workflowId, name, value }) {
        const workflowsSubSettings = useUserSettingsStore().settingByName(WORKFLOWS_SUB_USER_SETTING_NAME, true);

        // The name of the setting is `workflowsSettings--${subId}`
        const workflowsSubSettingsName = get(workflowsSubSettings, 'name', null);

        const workflowsSettingsMap = workflowsSubSettingsName ? cloneDeep(workflowsSubSettings.value) : {};

        set(workflowsSettingsMap, `${workflowId}.${name}`, value);

        useUserSettingsStore().update({
            name: WORKFLOWS_SUB_USER_SETTING_NAME,
            value: JSON.stringify(workflowsSettingsMap),
            namespaced: true,
        });
    },
};

export const getters = {
    list: (state) => Object.values(state.map),
    workflowById: (state) => (id) => get(state.map, id, null),
    savedWorkflow: (state, getters) => getters.workflowById(state.savedWorkflowId),
    workflowHistory: (state) => {
        return (
            useHistoryStore().itemHistory(HISTORY_TYPES.WORKFLOW)
                // the filter below removes any reports from the history that have been deleted and neccesitates that all workflows are available in state.map
                .filter(({ id }) => state.map[id])
                .map(({ id, timestamp }) => ({
                    id,
                    reportName: state.map[id].name,
                    createdBy: state.map[id].createdByUser.username,
                    description: state.map[id].description,
                    historyType: HISTORY_TYPES.WORKFLOW,
                    routeName: 'WorkflowDetails',
                    label: 'Workflow',
                    timestamp,
                    starred: false,
                }))
        );
    },
    savedWorkflowSettingsById: () => (id) => {
        const workflowId = id;
        const workflowsSubSettings = useUserSettingsStore().settingByName(WORKFLOWS_SUB_USER_SETTING_NAME, true);

        if (workflowId === 'new' || !workflowsSubSettings) return defaultWorkflowSettings;

        const { value: workflowsSettingsMap } = workflowsSubSettings;

        return workflowsSettingsMap[workflowId];
    },
    savedWorkflowSettings: (state) => {
        const workflowId = state.savedWorkflowId;
        const workflowsSubSettings = useUserSettingsStore().settingByName(WORKFLOWS_SUB_USER_SETTING_NAME, true);

        if (workflowId === 'new' || !workflowsSubSettings) return defaultWorkflowSettings;

        const { value: workflowsSettingsMap } = workflowsSubSettings;

        return workflowsSettingsMap[workflowId];
    },
    isLoading: (state) => state.isSaving || state.isFetching,
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
